/**
 * AnchorHighlight — B-2026-0088 Phase A3
 *
 * Renders a Portal-based SVG overlay that:
 *   1. Draws a "marching ants" highlight ring around the anchor element's rect.
 *   2. Draws a cubic-bezier arrow from the card's nearest edge to the anchor's
 *      nearest edge with a small arrowhead.
 *
 * The SVG is `position:fixed; inset:0; pointer-events:none` so it floats above
 * all page content without blocking any interactions. It sits at z-[9999], one
 * level below the guide card at z-[10000].
 *
 * Renders nothing when:
 *   - `show` is false (offscreen, covered, or no element found)
 *   - `anchorRect` is null (before first rAF fires)
 *   - `cardRect` is null (card not yet measured)
 *
 * Marching ants animation is a CSS keyframe on `stroke-dashoffset`.
 * Arrow path is a cubic-bezier curve from card edge midpoint to anchor edge midpoint.
 * Arrowhead is a small SVG polygon computed from the arrival direction.
 */

import Portal from '@/components/ui/Portal';

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/** Find the edge midpoint on rect closest to the other rect's centre. */
function nearestEdgeMidpoint(
  rect: DOMRect,
  toward: { x: number; y: number },
): { x: number; y: number; edge: 'top' | 'bottom' | 'left' | 'right' } {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const dx = toward.x - cx;
  const dy = toward.y - cy;

  // Determine which edge the vector points toward.
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);

  if (absDx > absDy) {
    // Horizontal bias
    if (dx > 0) return { x: rect.right, y: cy, edge: 'right' };
    return { x: rect.left, y: cy, edge: 'left' };
  } else {
    // Vertical bias
    if (dy > 0) return { x: cx, y: rect.bottom, edge: 'bottom' };
    return { x: cx, y: rect.top, edge: 'top' };
  }
}

/** Compute the control points for a cubic bezier that "bends" out of the source edge. */
function bezierControlPoints(
  src: { x: number; y: number; edge: 'top' | 'bottom' | 'left' | 'right' },
  dst: { x: number; y: number; edge: 'top' | 'bottom' | 'left' | 'right' },
): { cp1x: number; cp1y: number; cp2x: number; cp2y: number } {
  const STRENGTH = 80; // control point offset in px

  const edgeNormal: Record<string, { nx: number; ny: number }> = {
    top: { nx: 0, ny: -1 },
    bottom: { nx: 0, ny: 1 },
    left: { nx: -1, ny: 0 },
    right: { nx: 1, ny: 0 },
  };

  const srcN = edgeNormal[src.edge];
  const dstN = edgeNormal[dst.edge];

  // Both control points push OUTWARD along their element's edge normal so the
  // curve leaves the card and *arrives at the anchor* perpendicular from
  // outside. (Using -dstN here would pull cp2 inside the anchor and make the
  // shaft hook back on itself.)
  return {
    cp1x: src.x + srcN.nx * STRENGTH,
    cp1y: src.y + srcN.ny * STRENGTH,
    cp2x: dst.x + dstN.nx * STRENGTH,
    cp2y: dst.y + dstN.ny * STRENGTH,
  };
}

/** Compute arrowhead polygon at the destination point arriving from direction. */
function arrowhead(
  tip: { x: number; y: number },
  edge: 'top' | 'bottom' | 'left' | 'right',
): string {
  const SIZE = 7;
  const HALF = SIZE * 0.6;

  // Arrow points INTO the destination rect, so we go in the opposite normal direction.
  const dirs: Record<string, { dx: number; dy: number; px: number; py: number }> = {
    top:    { dx: 0, dy: -1, px: 1, py: 0 },
    bottom: { dx: 0, dy: 1,  px: 1, py: 0 },
    left:   { dx: -1, dy: 0, px: 0, py: 1 },
    right:  { dx: 1, dy: 0,  px: 0, py: 1 },
  };

  const { dx, dy, px, py } = dirs[edge];
  // `dirs[edge]` is the OUTWARD normal; the arrow must point INTO the element,
  // so the apex sits one SIZE inward from the edge and the base wings lie flush
  // on the edge midpoint (`tip`). This makes the sharp point lead into the
  // anchor rather than back toward the card.
  const apexX = tip.x - dx * SIZE;
  const apexY = tip.y - dy * SIZE;
  const p1x = tip.x + px * HALF;
  const p1y = tip.y + py * HALF;
  const p2x = tip.x - px * HALF;
  const p2y = tip.y - py * HALF;

  return `${apexX},${apexY} ${p1x},${p1y} ${p2x},${p2y}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AnchorHighlightProps {
  /** Whether to render the highlight. False = no DOM output. */
  show: boolean;
  /** Bounding rect of the anchor element. */
  anchorRect: DOMRect | null;
  /** Bounding rect of the guide card panel (source of the arrow). */
  cardRect: DOMRect | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AnchorHighlight({ show, anchorRect, cardRect }: AnchorHighlightProps) {
  if (!show || !anchorRect || !cardRect) return null;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1440;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 900;

  // Anchor element centre
  const anchorCx = anchorRect.left + anchorRect.width / 2;
  const anchorCy = anchorRect.top + anchorRect.height / 2;

  // Card centre
  const cardCx = cardRect.left + cardRect.width / 2;
  const cardCy = cardRect.top + cardRect.height / 2;

  // Arrow source: nearest edge of card toward anchor
  const src = nearestEdgeMidpoint(cardRect, { x: anchorCx, y: anchorCy });
  // Arrow destination: nearest edge of anchor toward card
  const dst = nearestEdgeMidpoint(anchorRect, { x: cardCx, y: cardCy });

  const { cp1x, cp1y, cp2x, cp2y } = bezierControlPoints(src, dst);
  const arrowPoints = arrowhead(dst, dst.edge);

  // Highlight ring: add 6px padding around anchor rect
  const PAD = 6;
  const rx = anchorRect.left - PAD;
  const ry = anchorRect.top - PAD;
  const rw = anchorRect.width + PAD * 2;
  const rh = anchorRect.height + PAD * 2;

  return (
    <Portal zIndex="z-[9999]">
      <svg
        style={{
          position: 'fixed',
          inset: 0,
          width: `${vw}px`,
          height: `${vh}px`,
          pointerEvents: 'none',
          overflow: 'visible',
        }}
        aria-hidden="true"
      >
        <defs>
          <style>{`
            @keyframes march {
              to { stroke-dashoffset: -20; }
            }
            .marching-ants {
              animation: march 0.6s linear infinite;
            }
          `}</style>
        </defs>

        {/* Highlight ring — marching ants */}
        <rect
          x={rx}
          y={ry}
          width={rw}
          height={rh}
          rx={8}
          ry={8}
          fill="none"
          stroke="var(--color-accent-cyan, #22d3ee)"
          strokeWidth={2}
          strokeOpacity={0.6}
          strokeDasharray={`10 10`}
          strokeDashoffset={0}
          className="marching-ants"
        />

        {/* Arrow shaft — cubic bezier */}
        <path
          d={`M ${src.x} ${src.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${dst.x} ${dst.y}`}
          fill="none"
          stroke="var(--color-accent-cyan, #22d3ee)"
          strokeWidth={1.5}
          strokeOpacity={0.55}
        />

        {/* Arrowhead */}
        <polygon
          points={arrowPoints}
          fill="var(--color-accent-cyan, #22d3ee)"
          fillOpacity={0.55}
        />
      </svg>
    </Portal>
  );
}

export default AnchorHighlight;
